// src/features/organisation/components/hierarchy/AdvancedHierarchyManagement.tsx
import React, { useState } from 'react';
import { GitBranch, Plus, Trash2, Share2 } from 'lucide-react';
import { apiClient } from '../../../../services/api/client';
import { TableColumn } from '../../../../types';
import DataTable from '../../../../components/ui/DataTable';
import OrganisationNavigation from '../OrganisationNavigation';
import HierarchyDetailForm from './HierarchyDetailForm';
import DelegationDetailForm from './DelegationDetailForm';

interface EmployeeSummary {
  id: number;
  firstName: string;
  lastName: string;
  employeeCode: string;
}

interface SiteSummary {
  id: number;
  siteName: string;
  siteCode: string;
}

interface EmployeeHierarchy {
  id: number;
  employee?: EmployeeSummary;
  manager?: EmployeeSummary;
  site?: SiteSummary;
  hierarchyTypeCode: string;
  responsibilityLevel: number;
  effectiveFrom: string;
  effectiveTo?: string;
  delegationReason?: string;
  isActive: boolean;
}

interface SiteDelegation {
  id: number;
  managerId: number;
  managerFirstName?: string;
  managerLastName?: string;
  supervisedSiteId: number;
  supervisedSiteName?: string;
  delegationType: string;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
}

const HIERARCHY_TYPE_LABELS: Record<string, string> = {
  FUNCTIONAL: 'Fonctionnelle',
  OPERATIONAL: 'Opérationnelle',
  ADMINISTRATIVE: 'Administrative',
};

const DELEGATION_TYPE_LABELS: Record<string, string> = {
  FULL: 'Complète',
  QHSE_ONLY: 'QHSE uniquement',
  DEPARTMENTAL: 'Départementale',
  TEMPORARY: 'Temporaire',
};

const AdvancedHierarchyManagement: React.FC = () => {
  const [hierarchies, setHierarchies] = useState<EmployeeHierarchy[]>([]);
  const [delegations, setDelegations] = useState<SiteDelegation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'hierarchies' | 'delegations'>('hierarchies');

  const [selectedHierarchyId, setSelectedHierarchyId] = useState<number | null | undefined>(undefined);
  const [selectedDelegationId, setSelectedDelegationId] = useState<number | null | undefined>(undefined);
  const [selectedHierarchies, setSelectedHierarchies] = useState<EmployeeHierarchy[]>([]);
  const [selectedDelegations, setSelectedDelegations] = useState<SiteDelegation[]>([]);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [h, d] = await Promise.all([
        apiClient.get<EmployeeHierarchy[]>('/organisation/employee-hierarchies').catch(() => [] as EmployeeHierarchy[]),
        apiClient.get<SiteDelegation[]>('/organisation/site-delegations').catch(() => [] as SiteDelegation[]),
      ]);
      setHierarchies(h || []);
      setDelegations(d || []);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const hierarchyColumns: TableColumn<EmployeeHierarchy>[] = [
    {
      key: 'employee',
      title: 'Employé',
      sortable: true,
      render: (value: EmployeeSummary) => value ? `${value.firstName} ${value.lastName}` : '-',
    },
    {
      key: 'manager',
      title: 'Manager',
      render: (value: EmployeeSummary) => value ? `${value.firstName} ${value.lastName}` : '-',
    },
    {
      key: 'site',
      title: 'Site',
      render: (value: SiteSummary) => value ? value.siteName : '-',
    },
    {
      key: 'hierarchyTypeCode',
      title: 'Type',
      render: (value: string) => HIERARCHY_TYPE_LABELS[value] || value,
    },
    {
      key: 'responsibilityLevel',
      title: 'Niveau',
      render: (value: number) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      key: 'effectiveFrom',
      title: "Date d'effet",
      render: (value: string) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
  ];

  const delegationColumns: TableColumn<SiteDelegation>[] = [
    {
      key: 'managerId',
      title: 'Manager',
      sortable: true,
      render: (_value: number, record: SiteDelegation) =>
        record.managerFirstName || record.managerLastName
          ? `${record.managerFirstName || ''} ${record.managerLastName || ''}`.trim()
          : '-',
    },
    {
      key: 'supervisedSiteId',
      title: 'Site supervisé',
      render: (_value: number, record: SiteDelegation) => record.supervisedSiteName || '-',
    },
    {
      key: 'delegationType',
      title: 'Type de délégation',
      render: (value: string) => DELEGATION_TYPE_LABELS[value] || value,
    },
    {
      key: 'effectiveFrom',
      title: "Date d'effet",
      render: (value: string) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
    {
      key: 'effectiveTo',
      title: 'Date de fin',
      render: (value: string) => value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
  ];

  const handleBulkDeleteHierarchies = async () => {
    if (selectedHierarchies.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedHierarchies.length} relation(s) hiérarchique(s) ?`)) return;
    try {
      await Promise.all(
        selectedHierarchies.map(h => apiClient.delete(`/organisation/employee-hierarchies/${h.id}`))
      );
      setSelectedHierarchies([]);
      await fetchData();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleBulkDeleteDelegations = async () => {
    if (selectedDelegations.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedDelegations.length} délégation(s) ?`)) return;
    try {
      await Promise.all(
        selectedDelegations.map(d => apiClient.delete(`/organisation/site-delegations/${d.id}`))
      );
      setSelectedDelegations([]);
      await fetchData();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleHierarchyClose = async () => {
    setSelectedHierarchyId(undefined);
    await fetchData();
  };

  const handleDelegationClose = async () => {
    setSelectedDelegationId(undefined);
    await fetchData();
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <GitBranch className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Hiérarchies</h1>
            <p className="text-gray-600">Relations hiérarchiques et délégations multi-sites</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {activeTab === 'hierarchies' && (
            <>
              {selectedHierarchies.length > 0 && (
                <button
                  onClick={handleBulkDeleteHierarchies}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Supprimer ({selectedHierarchies.length})</span>
                </button>
              )}
              <button
                onClick={() => setSelectedHierarchyId(null)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle relation</span>
              </button>
            </>
          )}
          {activeTab === 'delegations' && (
            <>
              {selectedDelegations.length > 0 && (
                <button
                  onClick={handleBulkDeleteDelegations}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Supprimer ({selectedDelegations.length})</span>
                </button>
              )}
              <button
                onClick={() => setSelectedDelegationId(null)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle délégation</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('hierarchies')}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'hierarchies'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GitBranch className="h-4 w-4" />
              Relations Hiérarchiques
              {hierarchies.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'hierarchies' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>{hierarchies.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('delegations')}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'delegations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Share2 className="h-4 w-4" />
              Délégations de Sites
              {delegations.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === 'delegations' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>{delegations.length}</span>
              )}
            </button>
          </nav>
        </div>

        {activeTab === 'hierarchies' && (
          <DataTable
            data={hierarchies}
            columns={hierarchyColumns}
            loading={loading}
            selectable={true}
            onSelectionChange={setSelectedHierarchies}
            onRowDoubleClick={(item) => setSelectedHierarchyId(item.id)}
            emptyMessage="Aucune relation hiérarchique trouvée"
          />
        )}

        {activeTab === 'delegations' && (
          <DataTable
            data={delegations}
            columns={delegationColumns}
            loading={loading}
            selectable={true}
            onSelectionChange={setSelectedDelegations}
            onRowDoubleClick={(item) => setSelectedDelegationId(item.id)}
            emptyMessage="Aucune délégation trouvée"
          />
        )}
      </div>

      {selectedHierarchyId !== undefined && (
        <HierarchyDetailForm
          hierarchyId={selectedHierarchyId}
          onClose={handleHierarchyClose}
          onSave={fetchData}
        />
      )}

      {selectedDelegationId !== undefined && (
        <DelegationDetailForm
          delegationId={selectedDelegationId}
          onClose={handleDelegationClose}
          onSave={fetchData}
        />
      )}
    </div>
  );
};

export default AdvancedHierarchyManagement;
